/**
 * ConnectionLine - Animated bezier curve connecting two entities
 *
 * Line Styles:
 * - Approved: Solid emerald line
 * - Suggested: Dashed amber line with marching ants animation
 * - Manual: Solid blue line (user-created)
 */

import { motion } from 'framer-motion';
import React from 'react';

import type { ConnectionLineData } from '../types/linking-view.types';
import { calculateBezierPath } from './bezierUtils';

interface ConnectionLineProps {
  data: ConnectionLineData;
}

/**
 * Get stroke styles based on connection status
 */
function getStrokeStyles(status: ConnectionLineData['status']): {
  stroke: string;
  strokeDasharray?: string;
  strokeWidth: number;
} {
  switch (status) {
    case 'approved':
      return {
        stroke: '#10b981', // emerald-500
        strokeWidth: 2,
      };
    case 'suggested':
      return {
        stroke: '#f59e0b', // amber-500
        strokeDasharray: '6 4',
        strokeWidth: 2,
      };
    case 'manual':
      return {
        stroke: '#3b82f6', // blue-500
        strokeWidth: 2,
      };
    case 'dragging':
      return {
        stroke: '#3b82f6', // blue-500
        strokeDasharray: '4 4',
        strokeWidth: 2,
      };
    default:
      return {
        stroke: '#a1a1aa', // zinc-400
        strokeWidth: 1,
      };
  }
}

export const ConnectionLine: React.FC<ConnectionLineProps> = ({ data }) => {
  const path = calculateBezierPath(data.start, data.end);
  const styles = getStrokeStyles(data.status);

  return (
    <motion.g
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
    >
      {/* Glow effect for approved links */}
      {data.status === 'approved' && (
        <motion.path
          d={path}
          fill="none"
          stroke="#10b981"
          strokeWidth={6}
          strokeOpacity={0.2}
          strokeLinecap="round"
          initial={{ pathLength: 0 }}
          animate={{ pathLength: 1 }}
          transition={{ duration: 0.5, ease: 'easeOut' }}
        />
      )}

      {/* Main line */}
      <motion.path
        d={path}
        fill="none"
        stroke={styles.stroke}
        strokeWidth={styles.strokeWidth}
        strokeDasharray={styles.strokeDasharray}
        strokeLinecap="round"
        initial={{ pathLength: 0 }}
        animate={{ pathLength: 1 }}
        transition={{ duration: 0.5, ease: 'easeOut' }}
      />

      {/* Animated dash for suggested links */}
      {data.status === 'suggested' && (
        <motion.path
          d={path}
          fill="none"
          stroke={styles.stroke}
          strokeWidth={styles.strokeWidth}
          strokeDasharray="6 4"
          strokeLinecap="round"
          animate={{
            strokeDashoffset: [0, -20],
          }}
          transition={{
            duration: 1,
            repeat: Infinity,
            ease: 'linear',
          }}
        />
      )}

      {/* Start dot */}
      <motion.circle
        cx={data.start.x}
        cy={data.start.y}
        r={4}
        fill={styles.stroke}
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ delay: 0.3, type: 'spring', stiffness: 300 }}
      />

      {/* End dot */}
      <motion.circle
        cx={data.end.x}
        cy={data.end.y}
        r={4}
        fill={styles.stroke}
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ delay: 0.4, type: 'spring', stiffness: 300 }}
      />
    </motion.g>
  );
};

export default ConnectionLine;
